(ns practitest.frontend.components2.spinners
  (:require [practitest.frontend.pt-ts-bridge :as pt-ts :refer [Loading]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.utils :refer [development-only example register-components!]]
            [practitest.frontend.components2.buttons :as buttons]
            [reagent.core :as reagent]))


(defn ^{:component? true} delayed-after-mount
  "Higher order component to add delay before mounting another component.

  Right now used with spinners, but theoretically can be used with any component.

  * `delay` - delay in milliseconds before mounting, default is 0, if 0 - mounted instantly (no delay)
  * `component-fn` - `(fn [delay-finished])` - callback that will be passed a
                     boolean indicating whether delay is finished or not"
  [delay component-fn]
  (let [delay (or delay 0)
        delay-finished (reagent/atom (if (= 0 delay)
                                       true
                                       false))
        timeout-id (atom nil)]
    (reagent/create-class
      {:component-did-mount    (fn [_]
                                 (when (> delay 0)
                                   (reset! timeout-id
                                           (js/setTimeout
                                             (fn []
                                               (reset! delay-finished true))
                                             delay))))
       :component-will-unmount (fn [_]
                                 (when-let [id @timeout-id]
                                   (js/clearTimeout id)))
       :reagent-render         (fn [_ component-fn]
                                 (component-fn @delay-finished))})))

;;
;; Design criterias for spinners
;;
;; 1. We want to show some kind of progress indicator when network operation is performed,
;;    otherwise it's not clear if UI is stuck or if something is going on
;; 2. We don't want to cause unnecessary blinking, resizing, re-layout effects because it's annoying
;; 3. In case we don't know how long operation will take we use spinners, otherwise - progress bars
;; 4. We don't really want to annoy user with spinners too much (especially if he/she is on good connection)
;;    so we should add delays before showing large spinners or overlay spinners
;; 5. On the other hand we still want to indicate that network operation completed successfully even
;;    if on-screen result haven't changed and even if it was really quick, so it's a good idea to show
;;    small absolute-positioned spinner somewhere on screen so user can see that system is working
;;
;; Quickest approach - show delayed inline spinner, delay should be correlated with debounce interval
;; (if applicable) default delay is 200ms. Inline spinner appearance is animated with fade-in effect as well
;;
;; Adjust as necessary for different use-cases
;;

(def spinner-sizes
  {:small  16
   :medium 36
   :large  48
   :xlarge 72})

;;
;; Questions to finalize
;;
;; 1. Sizes of spinners
;; 2. Whether to always use bezier? animation or not
;; 3. Whether to always use inside? flag or not
;;

(defn ^{:component? true}
  mux-spinner
  "Spinner component with MouseUX style.

  * `delay` - delay in milliseconds before showing component, delay starts after component is mounted
  * `size` - either predefined spinner size constant (e.g. `:small` / `:medium`) or size in pixels, e.g. 30
  * `bezier?` - use bezier animation function
  * `fade-in?` - do a fade-in animation when mounting spinner
  * `with-overlay?` - create overlay for spinner, must have position: relative/absolute parent
  * `inside?` - render inside part of the circle as well"
  [{:keys [delay size bezier? fade-in? with-overlay? inside?]
    :or {size :medium
         delay 0
         fade-in? true
         bezier? false
         inside? true
         with-overlay? false}
    :as props}]
  [delayed-after-mount
   delay
   (fn [delay-finished]
     (let [size (if (string? size)
                  (keyword size)
                  size)
           size-px (str (get spinner-sizes size size) "px")]
       (when delay-finished
         [:<>
          (when with-overlay?
            [:div.spinner-overlay])

          (let [spinner
                [:div
                 {:class (dom/classes-pairs-v :spinner-fade-in fade-in?)
                  :style {:width    size-px
                          :height   size-px
                          :overflow "hidden"}}
                 [:svg.new-spinner
                  {:class   (dom/classes-pairs-v :bezier bezier?)
                   :viewBox "0 0 16 16"
                   :width   size-px
                   :height  size-px
                   :xmlns   "http://www.w3.org/2000/svg"
                   :fill    "none"}
                  ;; Draw inside circle if required
                  (when inside?
                    [:circle {:cx 8 :cy 8 :r 7
                              :stroke "var(--pt-color-border-light)"
                              :style {:stroke-dasharray 0}}])
                  [:circle {:cx 8 :cy 8 :r 7}]]]]
            (if with-overlay?
              ;; Wrap with special div in overlay case
              [:div.spinner-holder
               spinner]
              ;; No overlay - just show spinner
              spinner))])))])

(development-only
  (defonce examples-state (reagent/atom {}))

  (defn spinners-section
    [header items]
    [:<>
     [:h4 header]
     [:div {:style {:display        "flex"
                    :flex-direction "row"
                    :gap            "1rem"}}
      (for [item items]
        [:div {:key   (:key item)
               :style {:display        "flex"
                       :flex-direction "column"
                       :align-items    "center"
                       :gap            "0.5rem"}}
         [:span (:title item)]
         (:component item)])]])

  (defn ^{:component-example mux-spinner}
    simple-spinner-examples
    []
    [:div
     [spinners-section
      "Different sizes, simple rotation"
      (for [size (concat (keys spinner-sizes) [25 50])]
        {:key       (str size)
         :title     (str size)
         :component [mux-spinner {:size size}]})]

     [spinners-section
      "Different sizes with special bezier animation function"
      (for [size (keys spinner-sizes)]
        {:key       (str size)
         :title     (str size)
         :component [mux-spinner {:size    size
                                  :bezier? true}]})]

     [:h3 "TypeScript spinner for comparison"]
     [:div
      [:r> Loading]]

     [:h3 "Spinner with delay"]

     [:p "All spinners have fade-in delay by default, but you can provide additional delay as
     a property if you want to delay showing of spinner more"]

     [buttons/button
      {:on-click (fn [e]
                   (swap! examples-state update :toggle not))
       :text "Toggle"}]

     [:h4 "Spinner without extra delay"]

     (when (:toggle @examples-state)
       (example
         [mux-spinner]))

     [:h4 "Spinner with extra 1000ms delay"]

     (when (:toggle @examples-state)
       (example
         [mux-spinner {:delay 1000}]))

     [:h4 "Spinner with fade-in animation effect removed"]

     (when (:toggle @examples-state)
       (example
         [mux-spinner {:delay    0
                       :fade-in? false}]))

     [:h4 "Spinner with overlay"]

     [:p "You can use spinner with overlay if you want to disable some content indicating
     progress, but don't want to clear screen every time. Note that it requires
     position: relative/absolute parent to position overlay correctly"]

     [:div {:style {:width    "400px"
                    :height   "200px"
                    :border   "1px solid black"
                    :padding  "5px"
                    :position "relative"}}
      (when (:toggle @examples-state)
        (example [mux-spinner {:with-overlay? true
                               :inside?       false
                               :size          :large}]))
      "Div with some content 3"

      ]]))

(register-components!)
